import React, { useMemo } from "react";
import { Combobox, Label } from "@/components/ui";
import { ContentPageTheme, Form } from "@/services/bedrock";

interface OptionType {
  label: string;
  value: string;
}

const PlayerDefaultCombobox: React.FC<{
  label: string;
  items: OptionType[];
  value?: number | null;
  emptyText: string;
  onChange: (value: number | null) => void;
}> = ({ label, items, value, emptyText, onChange }) => (
  <div className="flex flex-col gap-2">
    <Label>{label}</Label>
    <Combobox
      items={items}
      value={value ? items.find(item => item.value === String(value)) || null : null}
      onValueChange={(val: OptionType | null) => onChange(val ? Number(val.value) : null)}
      searchable
      showClear
      placeholder="Use Player default"
      emptyText={emptyText}
      isItemEqualToValue={(current, selected) => current?.value === selected?.value}
      itemToStringLabel={item => item?.label || ""}
    />
  </div>
);

const ThemePicker: React.FC<{
  themes: ContentPageTheme[];
  value?: number | null;
  onChange: (contentPageThemeId: number | null) => void;
}> = ({ themes, value, onChange }) => {
  const items = useMemo(() => themes.map(theme => ({ label: theme.name, value: String(theme.id) })), [themes]);
  return (
    <PlayerDefaultCombobox label="Theme" items={items} value={value} emptyText="No themes found" onChange={onChange} />
  );
};

const ReviewFormPicker: React.FC<{
  forms: Form[];
  value?: number | null;
  onChange: (formId: number | null) => void;
}> = ({ forms, value, onChange }) => {
  const items = useMemo(() => forms.map(form => ({ label: form.displayName, value: String(form.id) })), [forms]);
  return (
    <PlayerDefaultCombobox
      label="Review form"
      items={items}
      value={value}
      emptyText="No forms found"
      onChange={onChange}
    />
  );
};

const ThemeAndFormPickers: React.FC<{
  themes: ContentPageTheme[];
  forms: Form[];
  contentPageThemeId?: number | null;
  formId?: number | null;
  onChange: (updates: { contentPageThemeId?: number | null; formId?: number | null }) => void;
}> = ({ themes, forms, contentPageThemeId, formId, onChange }) => (
  <div className="grid gap-4 @min-[35rem]:grid-cols-2">
    <ThemePicker
      themes={themes}
      value={contentPageThemeId}
      onChange={value => onChange({ contentPageThemeId: value })}
    />
    <ReviewFormPicker forms={forms} value={formId} onChange={value => onChange({ formId: value })} />
  </div>
);

export { ThemeAndFormPickers, ThemePicker, ReviewFormPicker };
